import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Hash, Palette } from "lucide-react";
import { User as UserType } from "./HeatedGame";

type TopInformationBarProps = {
    gameCode: string | string[] | null;
    idOfPlayerWhoseTurnItIs: string;
    currentColor: string;
    users: UserType[];
}

export function TopInformationBar(props: TopInformationBarProps) {
    const [currentPlayerName, setCurrentPlayerName] = useState("")

    useEffect(() => {
        for (let u = 0; u < props.users.length; u++) {
            if (props.users[u].id === props.idOfPlayerWhoseTurnItIs) {
                setCurrentPlayerName(props.users[u].name)
            }
        }
    }, [props.idOfPlayerWhoseTurnItIs, props.users])

    const getColorClass = (color: string) => {
        switch(color.toLowerCase()) {
            case 'red': return 'red';
            case 'blue': return 'blue';
            case 'green': return 'green';
            case 'yellow': return 'yellow';
            case 'purple': return 'purple';
            default: return 'gray';
        }
    }

    const containerVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        }
    }

    return (
        <motion.div
            className="top-information-bar-modern"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <AnimatePresence mode="wait">
                {currentPlayerName && (
                    <motion.div
                        className="info-card"
                        variants={itemVariants}
                        key="player-turn"
                        layout
                    >
                        <Play className="info-icon" />
                        <div className="info-content">
                            <span className="info-label">Current Turn</span>
                            <motion.span
                                className="info-value"
                                key={currentPlayerName}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {currentPlayerName}
                            </motion.span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
                {currentPlayerName && props.currentColor && (
                    <motion.div
                        className={`info-card color-indicator color-${getColorClass(props.currentColor)}`}
                        variants={itemVariants}
                        key="current-color"
                        layout
                    >
                        <Palette className="info-icon" />
                        <div className="info-content">
                            <span className="info-label">Active Color</span>
                            <motion.span
                                className="info-value"
                                key={props.currentColor}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.3 }}
                            >
                                {props.currentColor}
                            </motion.span>
                        </div>
                        <motion.div
                            className="color-indicator-dot"
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.7, 1, 0.7]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                className="info-card"
                variants={itemVariants}
            >
                <Hash className="info-icon" />
                <div className="info-content">
                    <span className="info-label">Game Code</span>
                    <motion.span
                        className="info-value game-code"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {props.gameCode}
                    </motion.span>
                </div>
            </motion.div>

            <style>{`
                .top-information-bar-modern {
                    display: flex;
                    gap: var(--space-lg);
                    align-items: center;
                    flex-wrap: wrap;
                }

                .info-card {
                    display: flex;
                    align-items: center;
                    gap: var(--space-xs);
                    padding: var(--space-xs) var(--space-xs);
                    background: rgba(255, 255, 255, 0.08);
                    backdrop-filter: blur(15px);
                    border-radius: var(--radius-xl);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    transition: all var(--transition-normal);
                    position: relative;
                    overflow: hidden;
                }

                .info-card:hover {
                    background: rgba(255, 255, 255, 0.12);
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-lg);
                }

                .info-icon {
                    width: 1.25rem;
                    height: 1.25rem;
                    color: var(--gray-300);
                    flex-shrink: 0;
                }

                .info-content {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-xs);
                    min-width: 0;
                }

                .info-label {
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--gray-400);
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .info-value {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--gray-100);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .game-code {
                    font-family: monospace;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: var(--radius-md);
                    cursor: pointer;
                    user-select: all;
                }

                .color-indicator {
                    position: relative;
                }

                .color-indicator-dot {
                    width: 0.75rem;
                    height: 0.75rem;
                    border-radius: 50%;
                    position: absolute;
                    right: var(--space-sm);
                    top: 50%;
                    transform: translateY(-50%);
                }

                .color-indicator.color-red .color-indicator-dot {
                    background: var(--red-500);
                    box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
                }

                .color-indicator.color-blue .color-indicator-dot {
                    background: var(--blue-500);
                    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
                }

                .color-indicator.color-green .color-indicator-dot {
                    background: var(--green-500);
                    box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
                }

                .color-indicator.color-yellow .color-indicator-dot {
                    background: var(--yellow-500);
                    box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
                }

                .color-indicator.color-purple .color-indicator-dot {
                    background: var(--purple-500);
                    box-shadow: 0 0 10px rgba(139, 92, 246, 0.5);
                }

                .color-indicator.color-gray .color-indicator-dot {
                    background: var(--gray-500);
                    box-shadow: 0 0 10px rgba(107, 114, 128, 0.5);
                }

                @media (max-width: 768px) {
                    .top-information-bar-modern {
                        gap: var(--space-sm);
                    }

                    .info-card {
                        padding: var(--space-sm) var(--space-md);
                        gap: var(--space-sm);
                    }

                    .info-icon {
                        width: 1rem;
                        height: 1rem;
                    }

                    .info-label {
                        font-size: 0.6875rem;
                    }

                    .info-value {
                        font-size: 0.75rem;
                    }
                }
            `}</style>
        </motion.div>
    );
}
